import { TextInput } from "flowbite-react";
import moment from "moment";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

const accessNested = (obj, key) =>
  key
    .split(".")
    .reduce(
      (_obj, _key) => (!!_obj && typeof _obj === "object" ? _obj[_key] : null),
      obj
    );

function TextBox({
  attributes = {},
  name,
  placeholder,
  label,
  formik,
  readOnly,
  disabled,
  type = "text",
  showError = true,
  className,
  id = "",
  suffix,
  copyAction,
  value,
  required,
  error,
  onChange,
  autoComplete = "off",
  hidePasswordToggle = false,
}) {
  const [visible, setVisibility] = useState(false);
  const formikValue =
    type === "date" 
      ? moment(formik ? accessNested(formik.values, name || id) : value).format(
          "YYYY-MM-DD"
        )
      : formik
        ? accessNested(formik.values, name || id)
        : value ;

  const formikError = formik ? accessNested(formik.errors, name || id) : "";
  const formikTouched = formik ? accessNested(formik.touched, name || id) : "";
  const _required = required === false ? false : formik ? formik.isFieldRequired(name || id) : required;
  const isAdditionalValidationAppended = useRef(false);

  useEffect(() => {
    if(!isAdditionalValidationAppended.current){
      // Adding range validation to the input
      if (type === 'number' && attributes && (Number.isInteger(attributes?.min) || Number.isInteger(attributes?.max))) {
        isAdditionalValidationAppended.current = false;
        // Adding validation to the field
        formik.registerField(name || id, {
          validate: (value) => {
            if (Number.isInteger(attributes.min) && parseInt(value) < attributes.min) {
              return `Value can't be lesser than ${attributes.min}`;
            }
  
            if (Number.isInteger(attributes.max) && parseInt(value) > attributes.max) {
              return `Value can't be greater than ${attributes.max}`;
            }
            return false;
          }
        });
        return () => formik.unregisterField(name || id);
      // Adding phone number validation to the input
      } else if (type === 'tel') {
        isAdditionalValidationAppended.current = false;
        formik.registerField(name || id, {
          validate: (value) => {
            const phone_regex = new RegExp(/^[+\d()\s\-.]*$/);
            // Allow empty string and non-string types
            if (typeof value !== 'string' || !value) return false;
            const digitsArr = [...value.toString()].filter(char => char.trim().toString() && !isNaN(Number(char))); // Retrieving only the the number
            if (digitsArr.length > 15) return "Phone number can't be greater than 15 digits";
            // Check if only allowed characters are there
            if (!phone_regex.test(value)) return "Please enter a valid phone number";
            return false;
          }
        });
        return () => formik.unregisterField(name || id);
      }
    }
  }, [formik])

  const copyValue = () => {
    if (typeof navigator.clipboard == "undefined") {
      console.log("navigator.clipboard");
      var textArea = document.createElement("textarea");
      textArea.value = formikValue;
      textArea.style.position = "fixed"; //avoid scrolling to bottom
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        var successful = document.execCommand("copy");
        var msg = successful
          ? "Copied to clipboard"
          : "Can't copy at the moment.";
        toast.success(msg);
      } catch (err) {
        toast.warning("Can't copy at the moment.");
      }

      document.body.removeChild(textArea);
      return;
    }
    navigator.clipboard.writeText(formikValue).then(
      function () {
        toast.success(`Copied to clipboard`);
      },
      function (err) {
        toast.warn("Can't copy at the moment.");
      }
    );
  };

  return (
    <div className={className + " " + name}>
      {label && (
        <label
          className={`text-sm font-medium text-gray-900 dark:text-gray-300 ${
            _required && !readOnly
              ? "after:content-['_*'] after:text-red-500"
              : ""
          }`}
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <div
        className={`[&:hover_.copy-btn]:block  cursor-pointer mt-2 wrapper group ${
          readOnly ? "" : ""
        }`}
      >
        <TextInput
          id={id}
          disabled={disabled}
          name={name}
          readOnly={readOnly}
          placeholder={readOnly ? "----" : placeholder}
          onClick={() => {
            readOnly && copyAction && copyValue();
          }}
          className={`${
            readOnly
              ? "[&_input]:!rounded-none [&_input]:border-none [&_input]:p-0 [&_input:focus]:border-none [&_input:focus]:ring-0 [&_input]:text-gray-700"
              : ""
          } ${
            copyAction && readOnly && "[&_input]:cursor-pointer"
          } [&_input]:bg-transparent`}
          type={
            type === "password"
              ? visible
                ? "text"
                : "password"
              : readOnly
                ? "text"
                : type
          }
          autoComplete={autoComplete}
          value={formikValue || ''}
          onChange={(e) => {
            if (formik) {
              formik.handleChange(e);
              setTimeout(() => formik.setFieldTouched(name || id, true));
            }
            onChange && onChange(e);
          }}
        />
        {!readOnly && suffix && (
          <div className="relative">
            <button className="text-gray-900 absolute right-2.5 bottom-[5px]  focus:outline-none rounded-lg text-sm px-2 py-2 mt-4">
              {suffix}
            </button>
          </div>
        )}
        {type === "password" && !hidePasswordToggle && (
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setVisibility(!visible);
              }}
              className={`text-black absolute right-2.5 ${
                !readOnly ? "bottom-[7px]" : "bottom-[-7px]"
              }  focus:outline-none font-medium rounded-lg text-sm px-2 py-2 mt-4`}
            >
              {visible ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-3 h-3 cursor-pointer z-10"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-3 h-3 cursor-pointer z-10"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
            </button>
          </div>
        )}
        {type !== "password" && readOnly && copyAction && (
          <div className="hidden relative copy-btn">
            <button
              type="button"
              onClick={copyValue}
              className={`text-black absolute right-2.5 ${
                !readOnly ? "bottom-[7px]" : "bottom-[-7px]"
              }  focus:outline-none font-medium rounded-lg text-sm px-2 py-2 mt-4`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4 cursor-pointer z-10"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
      {showError && (formik?.touched ? formikTouched : !!error) && (
        <p className="first-letter:uppercase mt-2 text-sm text-red-500">
          {formik?.errors ? formikError : error || ""}
        </p>
      )}
    </div>
  );
}

export default TextBox;
